import { directoryAssetIdentity } from '../../utils/directoryAssetIdentity';
import { Fragment, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Globe, Monitor, Globe2, Cloud, User } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useAsset, useAssetRelationships } from '../../hooks/useClientData';
import { useAssetFindings } from '../../hooks/useAssetFindings';
import { useClient } from '../../context/ClientContext';
import { isDemoEnvironmentClient } from '../../data/demoAdSeed';
import { relativeTime, exactTime } from '../../utils/time';
import { assetLastSeenAt, getAssetLifecycle } from '../../utils/assetLifecycle';
import { linkedFindingsForAsset } from '../../utils/assetFindingLinks';
import { summarizeAssetRelationships } from '../../utils/assetRelationships';
import { groupByTitle, locationOf, targetLabel } from '../../utils/findingTarget';
import { adDirectoryDisplayName, adDirectoryIdentity, assetDisplayText, assetMetadataLabel, assetPresentationType, isAdFindingAggregate, preferredAssetName } from './Assets';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import SeverityBadge from '../../components/badges/SeverityBadge';
import StatusBadge from '../../components/badges/StatusBadge';
import Skeleton from '../../components/ui/Skeleton';
import { TR, TD } from '../../components/ui/Table';

const ASSET_TYPE_LABELS = { 1: 'External IP', 2: 'Internal Host', 3: 'Domain', 4: 'Cloud Resource', 5: 'Account', 6: 'External Host' };
const ASSET_TYPE_COLORS = { 1: C.org, 2: C.blu, 3: C.amb, 4: C.pur, 5: C.sky, 6: C.org };
const ASSET_TYPE_ICONS = { 1: Globe, 2: Monitor, 3: Globe2, 4: Cloud, 5: User, 6: Globe };
const ASSET_TYPE_STR_MAP = {
  externalip: 1, internalhost: 2, domain: 3, cloudresource: 4, user: 5,
  external_ip: 1, internal_host: 2, cloud_resource: 4, account: 5,
};
const LIFECYCLE_COLORS = {
  active: C.grn,
  stale: C.amb,
  retired: C.t3,
  unknown: C.t2,
};
const MAX_TAGS_RENDERED = 30;
const MAX_METADATA_ROWS_RENDERED = 25;
const MAX_FINDING_GROUPS_RENDERED = 100;
const MAX_EXPANDED_FINDINGS_RENDERED = 50;

const normalizeAssetType = (t) => {
  if (typeof t === 'number') return t;
  if (typeof t === 'string') return ASSET_TYPE_STR_MAP[t.toLowerCase()] ?? 0;
  return 0;
};

function parseTag(tagStr) {
  if (!tagStr || typeof tagStr !== 'string') return null;
  const parts = tagStr.split(':');
  return parts[2] || parts[1] || parts[0] || null;
}

export function normalizeAssetDetailPayload(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

  const candidate = payload.asset ?? payload.item ?? payload.data ?? payload;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;

  const hasAssetShape = Boolean(
    candidate.assetId ||
    candidate.id ||
    candidate.externalId ||
    candidate.displayName ||
    candidate.hostname ||
    candidate.ipAddress
  );
  return hasAssetShape ? candidate : null;
}

export function limitedAssetMetadataEntries(asset, maxRows = MAX_METADATA_ROWS_RENDERED) {
  let metadata = null;
  if (asset?.metadataJson) {
    try {
      metadata = typeof asset.metadataJson === 'string' ? JSON.parse(asset.metadataJson) : asset.metadataJson;
    } catch { /* not valid JSON */ }
  }
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return { entries: [], truncated: false };
  const entries = Object.entries(metadata);
  return { entries: entries.slice(0, maxRows), truncated: entries.length > maxRows };
}

function metadataObject(asset) {
  if (!asset?.metadataJson) return {};
  if (typeof asset.metadataJson === 'object' && !Array.isArray(asset.metadataJson)) return asset.metadataJson;
  try {
    const parsed = JSON.parse(asset.metadataJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function firstValue(...values) {
  return values.find(value => value !== undefined && value !== null && String(value).trim() !== '');
}

function listValue(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch { /* ignore */ }
    return value.split(/[,;\n]/).map(v => v.trim()).filter(Boolean);
  }
  if (typeof value === 'object') return Object.entries(value).map(([k, v]) => `${k}: ${v}`);
  return [String(value)];
}

function stagedDepthForAsset(asset, isDemo) {
  const md = metadataObject(asset);
  const name = String(asset?.displayName || asset?.hostname || asset?.assetId || '').toUpperCase();
  const typeNum = normalizeAssetType(asset?.assetType);
  const lanIp = firstValue(asset?.lanIp, asset?.localIp, asset?.privateIp, asset?.privateIpAddress, md.lanIp, md.localIp, md.privateIp, md.privateIpAddress, md.hostIp, asset?.ipAddress);
  const wanIp = firstValue(asset?.wanIp, asset?.publicIp, asset?.externalIp, asset?.publicIpAddress, md.wanIp, md.publicIp, md.externalIp, md.publicIpAddress);
  const mac = firstValue(asset?.macAddress, asset?.mac, md.macAddress, md.mac, md.physicalAddress);
  const user = firstValue(asset?.user, asset?.username, asset?.owner, asset?.primaryUser, md.user, md.username, md.owner, md.primaryUser, md.lastLoggedOnUser);
  let software = [
    ...listValue(asset?.software),
    ...listValue(asset?.installedSoftware),
    ...listValue(md.software),
    ...listValue(md.installedSoftware),
    ...listValue(md.applications),
  ];
  let permissions = [
    ...listValue(asset?.localPermissions),
    ...listValue(asset?.permissions),
    ...listValue(md.localPermissions),
    ...listValue(md.permissions),
    ...listValue(md.localAdministrators),
    ...listValue(md.adminGroups),
  ];

  if (isDemo && name.includes('GUS-DYNABOOK')) {
    return {
      lanIp: lanIp || '10.100.12.44',
      wanIp: wanIp || '198.51.100.44',
      mac: mac || '3C:58:C2:9A:41:0F',
      user: user || 'gus.elliott',
      software: software.length ? software : ['Windows 11 Pro', 'Microsoft 365 Apps', 'SentinelOne Agent', 'Nessus Agent', 'Chrome', 'QuickBooks Desktop'],
      permissions: permissions.length ? permissions : ['Local Administrators: Domain Admins', 'Remote Desktop Users: Helpdesk Tier 1', 'BitLocker recovery escrowed'],
    };
  }

  if (isDemo && software.length === 0) {
    software = typeNum === 4
      ? ['CloudTrail', 'Defender for Cloud policy', 'Key Vault diagnostic settings']
      : typeNum === 5
        ? ['Microsoft Entra ID', 'MFA registration', 'Conditional Access scope']
        : typeNum === 3
          ? ['DNS hosting', 'TLS certificate monitor', 'DMARC/SPF policy']
          : ['Operating system inventory pending', 'EDR agent expected', 'Vulnerability scanner coverage expected'];
  }
  if (isDemo && permissions.length === 0) {
    permissions = typeNum === 4
      ? ['Owner: Cloud Operations', 'Privileged role review required', 'Policy assignment inherited']
      : typeNum === 5
        ? ['Assigned roles: User', 'Privileged group membership review required', 'MFA required']
        : typeNum === 3
          ? ['Registrar admin: restricted', 'DNS editors: Platform Operations', 'Certificate renewal owner assigned']
          : ['Local Administrators: IT Operations', 'Remote access: monitored', 'Service logon rights under review'];
  }

  return {
    lanIp: lanIp || (isDemo && typeNum === 2 ? '10.100.x.x' : ''),
    wanIp: wanIp || (isDemo && typeNum === 1 ? asset?.ipAddress : ''),
    mac: mac || (isDemo && typeNum === 2 ? 'Captured by endpoint inventory when agent is online' : ''),
    user: user || (isDemo ? (typeNum === 5 ? name.toLowerCase() : 'Owner pending confirmation') : ''),
    software,
    permissions,
  };
}

function formatAssetDetailError(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return error.message || error.title || error.detail || 'Asset details are unavailable.';
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
      <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: mono ? FONTS.mono : FONTS.sans, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function LinkBadge({ type, reason }) {
  const authoritative = type === 'authoritative';
  const color = authoritative ? C.grn : C.amb;
  return (
    <span title={reason} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 9, fontWeight: 800, fontFamily: FONTS.mono,
      color,
      background: `${color}15`,
      padding: '2px 7px',
      borderRadius: 3,
      border: `1px solid ${color}33`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: color }} />
      {authoritative ? 'Backend link' : 'Inferred'}
    </span>
  );
}

function lifecycleSignalText(lifecycle) {
  if (!lifecycle) return 'Lifecycle state is being evaluated.';
  if (lifecycle.key === 'active') return 'Recently observed by connected data sources.';
  if (lifecycle.key === 'stale') return 'Not observed recently; review ownership and scan coverage.';
  if (lifecycle.key === 'retired') return lifecycle.label === 'Inactive'
    ? 'Marked inactive by inventory data.'
    : 'Marked retired or decommissioned.';
  return 'Lifecycle state has not been confirmed by recent scan data.';
}

function clientGraphLabel(total) {
  return total === 1 ? '1 relationship' : `${total} relationships`;
}

export function clientSafeDirectoryAssetDetail(asset) {
  // Canonical inventory retains the full asset/history view, including real endpoint hostnames.
  if (directoryAssetIdentity(asset)) return null;
  if (isAdFindingAggregate(asset)) {
    return {
      kind: 'consolidated',
      title: 'Consolidated directory evidence',
      subtitle: 'AD assessment record',
      message: 'This technical assessment record is consolidated into the canonical Accounts inventory. Review the related risk in AD Security.',
    };
  }

  const identity = adDirectoryIdentity(asset);
  if (!identity) return null;

  const typeLabels = { computer: 'AD Computer', group: 'AD Group', user: 'AD User' };
  return {
    kind: identity.kind,
    title: assetDisplayText(identity.name, '\u2014', asset?.assetId),
    subtitle: typeLabels[identity.kind] || 'Directory identity',
    message: 'Directory evidence is normalized for client review. Distinguished names and assessment details remain available in AD Security.',
  };
}

export default function AssetDetail() {
  const { id } = useParams();
  return <HydratedAssetDetail assetId={id} />;
}

function HydratedAssetDetail({ assetId }) {
  const navigate = useNavigate();
  const { data: rawAsset, loading, error } = useAsset(assetId);
  const asset = normalizeAssetDetailPayload(rawAsset);

  if (loading) return <AssetDetailSkeleton navigate={navigate} />;

  if (!asset || error) return (
    <Shell title="Asset Not Found">
      <div style={{ padding: 40, textAlign: 'center', color: C.t2 }}>
        {error ? `Error: ${formatAssetDetailError(error)}` : 'Asset detail is not available.'}{' '}
        <button onClick={() => navigate('/client/assets')} style={{ color: C.sky, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONTS.mono }}><ArrowLeft size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Back</button>
      </div>
    </Shell>
  );

  const clientSafeDirectoryDetail = clientSafeDirectoryAssetDetail(asset);
  if (clientSafeDirectoryDetail) {
    return (
      <Shell title={clientSafeDirectoryDetail.title} sub={clientSafeDirectoryDetail.subtitle}>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={() => navigate('/client/assets')}><ArrowLeft size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Back to Assets</Button>
          <Button variant="ghost" onClick={() => navigate('/client/ad-security')}>Review AD Security</Button>
        </div>
        <Card style={{ padding: 22 }}>
          <div style={{ fontSize: 12, lineHeight: 1.6, color: C.t1 }}>{clientSafeDirectoryDetail.message}</div>
          <div style={{ marginTop: 14, fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
            Canonical asset ID: {asset.assetId || '\u2014'}
          </div>
        </Card>
      </Shell>
    );
  }

  return <AssetDetailFullContent key={asset.assetId ?? asset.id} asset={asset} />;
}

function AssetDetailSkeleton({ navigate }) {
  return (
    <Shell title="Loading...">
      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <Button variant="ghost" onClick={() => navigate('/client/assets')}><ArrowLeft size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Back to Assets</Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <Card style={{ padding: 22 }}>
          <Skeleton width="60%" height={20} style={{ marginBottom: 12 }} />
          <Skeleton width="40%" height={14} style={{ marginBottom: 20 }} />
          <Skeleton height={120} />
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card style={{ padding: 14 }}><Skeleton height={120} /></Card>
          <Card style={{ padding: 14 }}><Skeleton height={100} /></Card>
        </div>
      </div>
    </Shell>
  );
}

function AssetDetailFullContent({ asset }) {
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [visibleGroupLimit, setVisibleGroupLimit] = useState(MAX_FINDING_GROUPS_RENDERED);
  const [expandedLimits, setExpandedLimits] = useState({});
  const { data: relationshipGraph, loading: relationshipsLoading, error: relationshipsError } = useAssetRelationships();
  const { findings: findingsToProcess, links: findingLinks, loading: findingsLoading, error: findingsError,
    total: backendFindingTotal, complete: findingsComplete, hasMore: hasMoreFindings,
    mode: findingsMode, checked: findingsChecked, sourceTotal, coverageWarning, loadMore, refetch: refreshFindings } = useAssetFindings(asset.assetId ?? asset.id);
  const { clientId, clientName } = useClient();
  const isDemo = isDemoEnvironmentClient(clientId, clientName);

  const relationshipSummary = useMemo(
    () => relationshipsLoading || relationshipsError ? { relationships: [], total: 0, inbound: 0, outbound: 0, byType: [] } : summarizeAssetRelationships(relationshipGraph, asset),
    [relationshipGraph, asset, relationshipsLoading, relationshipsError]
  );
  const linkedFindingResult = useMemo(
    () => findingsMode === 'legacy' ? {
      rows: findingLinks,
      counts: findingLinks.reduce((counts, link) => {
        counts[link.linkType]++;
        return counts;
      }, { authoritative: 0, inferred: 0 }),
    } : linkedFindingsForAsset({ ...asset, findings: findingsToProcess }, findingsToProcess),
    [asset, findingsToProcess, findingLinks, findingsMode]
  );
  const linkedFindings = useMemo(() => linkedFindingResult.rows.map(row => ({
    ...row.finding,
    linkType: row.linkType,
    linkReason: row.linkReason,
  })), [linkedFindingResult]);
  const linkCounts = linkedFindingResult.counts;

  const sevCounts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    for (const f of linkedFindings) c[f.sev] = (c[f.sev] || 0) + 1;
    return c;
  }, [linkedFindings]);

  const totalLinked = linkedFindings.length;
  const ownershipIncomplete = totalLinked !== findingsToProcess.length;
  const linkedGroups = useMemo(() => groupByTitle(linkedFindings), [linkedFindings]);
  const visibleLinkedGroups = useMemo(() => linkedGroups.slice(0, visibleGroupLimit), [linkedGroups, visibleGroupLimit]);
  const linkedGroupsTruncated = linkedGroups.length > visibleLinkedGroups.length;
  const toggleGroup = key => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const typeNum = normalizeAssetType(asset.assetType);
  const presentationTypeNum = assetPresentationType(asset);
  const findingSummary = isAdFindingAggregate(asset);
  const directoryIdentity = adDirectoryIdentity(asset);
  const metadataValues = metadataObject(asset);
  const directoryTypeLabels = { computer: 'AD Computer', group: 'AD Group', user: 'AD User' };
  const typeLabel = findingSummary
    ? 'AD Finding Summary'
    : directoryIdentity
      ? directoryTypeLabels[directoryIdentity.kind]
      : ASSET_TYPE_LABELS[presentationTypeNum] || `Type ${asset.assetType}`;
  const typeColor = findingSummary ? C.amb : directoryIdentity?.kind === 'computer' ? C.blu : ASSET_TYPE_COLORS[presentationTypeNum] || C.t2;
  const preferredName = findingSummary
    ? firstValue(metadataValues.findingTitle, asset.displayName, asset.hostname, asset.assetId)
    : adDirectoryDisplayName(asset) || preferredAssetName(asset);
  const name = assetDisplayText(String(preferredName || ''), '\u2014', asset.assetId);
  const parsedTags = Array.isArray(asset.tags) ? asset.tags.map(parseTag).filter(Boolean) : [];
  const tags = parsedTags.slice(0, MAX_TAGS_RENDERED);
  const tagsTruncated = parsedTags.length > tags.length;
  const lifecycle = getAssetLifecycle(asset);
  const lifecycleColor = LIFECYCLE_COLORS[lifecycle.key] || C.t2;
  const lastSeenAt = assetLastSeenAt(asset);
  const metadata = limitedAssetMetadataEntries(asset);
  const stagedDepth = stagedDepthForAsset(asset, isDemo);

  return (
    <Shell title={name} sub={`${typeLabel} ${asset.ipAddress ? `\u2014 ${asset.ipAddress}` : ''}`}>
      <div style={{ marginBottom: 12 }}>
        <Button variant="ghost" onClick={() => navigate('/client/assets')}><ArrowLeft size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Back to Assets</Button>
      </div>

      {findingSummary && (
        <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 6, border: `1px solid ${C.amb}55`, background: `${C.amb}10`, color: C.t1, fontSize: 11, lineHeight: 1.5 }}>
          This legacy record summarizes identities referenced by an Active Directory finding and is retained as source evidence. It is consolidated out of the Asset Inventory because it is not one canonical account. A directory identity, sometimes called a principal, can be a user, group, computer, or service account.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Section 1: Asset Overview */}
          <Card style={{ padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                background: `linear-gradient(135deg, ${typeColor}22, ${typeColor}08)`,
                border: `2px solid ${typeColor}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: typeColor,
              }}>
                {(() => {
                  const Ico = ASSET_TYPE_ICONS[presentationTypeNum] || User;
                  return <Ico size={22} strokeWidth={1.75} />;
                })()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>{name}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: typeColor, fontFamily: FONTS.mono,
                    background: `${typeColor}15`, padding: '2px 8px', borderRadius: 3,
                    border: `1px solid ${typeColor}33`,
                  }}>{typeLabel}</span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 9, fontWeight: 700, fontFamily: FONTS.mono,
                    color: lifecycleColor,
                    background: `${lifecycleColor}15`,
                    padding: '2px 8px', borderRadius: 3,
                    border: `1px solid ${lifecycleColor}33`,
                  }} title={lifecycle.reason}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: lifecycleColor }} />
                    {lifecycle.label}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 0, marginBottom: 14 }}>
              {stagedDepth.lanIp && <InfoRow label="LAN IP" value={stagedDepth.lanIp} mono />}
              {stagedDepth.wanIp && <InfoRow label="WAN IP" value={stagedDepth.wanIp} mono />}
              {stagedDepth.mac && <InfoRow label="MAC Address" value={stagedDepth.mac} mono />}
              {stagedDepth.user && <InfoRow label="Primary User / Owner" value={stagedDepth.user} mono />}
              {asset.ipAddress && <InfoRow label="IP Address" value={asset.ipAddress} mono />}
              {asset.hostname && <InfoRow label="Hostname" value={asset.hostname} mono />}
              <InfoRow label="First Seen" value={asset.firstSeenAt ? relativeTime(asset.firstSeenAt) : '\u2014'} mono />
              <InfoRow label="Last Seen" value={lastSeenAt ? relativeTime(lastSeenAt) : '\u2014'} mono />
              <InfoRow label="Asset ID" value={asset.assetId || '\u2014'} mono />
            </div>

            <div style={{ padding: '10px 12px', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, marginBottom: tags.length > 0 ? 14 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 3 }}>Lifecycle signal</div>
                  <div style={{ fontSize: 12, color: C.t1, fontWeight: 700 }}>{lifecycleSignalText(lifecycle)}</div>
                </div>
                <Tag label={lifecycle.needsAction ? 'Review inventory ownership' : 'No lifecycle action'} color={lifecycle.needsAction ? C.amb : C.grn} />
              </div>
            </div>

            {tags.length > 0 && (
              <div>
                <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>Tags</div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {tags.map((t, i) => <Tag key={i} label={t} color={C.sky} />)}
                  {tagsTruncated && <Tag label={`+${parsedTags.length - tags.length} more`} color={C.t3} />}
                </div>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Endpoint / Ownership Depth"
              sub="Inventory, installed software, and local permission context staged for analyst review."
            />
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Installed Software</div>
                {stagedDepth.software.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {stagedDepth.software.slice(0, 8).map(item => (
                      <div key={item} style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, padding: '6px 8px', border: `1px solid ${C.b2}`, borderRadius: 5, background: C.s2, wordBreak: 'break-word' }}>{item}</div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono }}>Software inventory not returned for this asset.</div>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Local Permissions / Access</div>
                {stagedDepth.permissions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {stagedDepth.permissions.slice(0, 8).map(item => (
                      <div key={item} style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, padding: '6px 8px', border: `1px solid ${C.b2}`, borderRadius: 5, background: C.s2, wordBreak: 'break-word' }}>{item}</div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono }}>Permission inventory not returned for this asset.</div>
                )}
              </div>
            </div>
          </Card>

          {/* Section 2: Metadata */}
          {metadata.entries.length > 0 && (
            <Card>
              <CardHeader title="Metadata" />
              <div style={{ padding: 16 }}>
                {metadata.entries.map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                    <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{assetMetadataLabel(k)}</span>
                    <span style={{ fontSize: 10, color: C.t1, fontFamily: FONTS.mono, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                  </div>
                ))}
                {metadata.truncated && (
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginTop: 8 }}>
                    Showing {metadata.entries.length} metadata fields.
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Section 3: Backend Asset Graph */}
          <Card>
            <CardHeader title="Asset Relationships" action={
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                {relationshipsLoading ? 'Loading relationships...' : clientGraphLabel(relationshipSummary.total)}
              </span>
            } />
            {relationshipsError ? (
              <div style={{ padding: '18px 16px', fontSize: 11, color: C.amb, fontFamily: FONTS.mono }}>
                Backend asset graph is unavailable for this client.
              </div>
            ) : relationshipSummary.total === 0 ? (
              <div style={{ padding: '22px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.t2, fontFamily: FONTS.sans }}>No Relationships Yet</div>
                <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, marginTop: 4 }}>Connected findings are shown below while asset relationships are being built from scan data.</div>
              </div>
            ) : (
              <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 }}>
                  {[
                    { l: 'Total', v: relationshipSummary.total, c: C.sky },
                    { l: 'Inbound', v: relationshipSummary.inbound, c: C.grn },
                    { l: 'Outbound', v: relationshipSummary.outbound, c: C.amb },
                  ].map(s => (
                    <div key={s.l} style={{ padding: '9px 10px', background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}` }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.v > 0 ? s.c : C.t3, fontFamily: FONTS.mono }}>{s.v}</div>
                      <div style={{ fontSize: 8, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase' }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                {relationshipSummary.byType.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
                    {relationshipSummary.byType.slice(0, 5).map(item => (
                      <Tag key={item.type} label={`${item.type}: ${item.count}`} color={C.sky} />
                    ))}
                  </div>
                )}
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                      {['Direction', 'Relationship', 'Related Asset', 'Source'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left', background: C.s2 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {relationshipSummary.relationships.slice(0, 8).map(row => (
                      <TR key={row.id} hover={false}>
                        <TD><Tag label={row.direction === 'inbound' ? 'Inbound' : row.direction === 'outbound' ? 'Outbound' : 'Self'} color={row.direction === 'inbound' ? C.grn : C.amb} /></TD>
                        <TD><span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{row.typeLabel}</span></TD>
                        <TD>
                          <div style={{ fontSize: 12, color: C.t1, fontWeight: 600 }}>{row.related.name || row.related.id}</div>
                          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{row.related.id}{row.related.type ? ` / ${row.related.type}` : ''}</div>
                        </TD>
                        <TD><span style={{ fontSize: 9, color: C.grn, fontFamily: FONTS.mono }}>{row.sourceSystem || 'Backend graph'}</span></TD>
                      </TR>
                    ))}
                  </tbody>
                </table>
                {relationshipSummary.total > 8 && (
                  <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginTop: 8 }}>
                    Showing 8 of {relationshipSummary.total} backend graph relationships.
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Section 3: Linked Findings */}
          <Card>
            <CardHeader title="Linked Findings" action={
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                {findingsMode === 'legacy'
                  ? `${totalLinked} linked / ${findingsChecked} of ${sourceTotal ?? 'unknown'} tenant findings checked / total unverified`
                  : backendFindingTotal === null ? (findingsLoading ? 'Loading findings...' : 'Finding count unavailable') : `${totalLinked} of ${backendFindingTotal} loaded / ${linkedGroups.length} unique`}
                {totalLinked > 0 ? ` / ${linkCounts.authoritative} backend / ${linkCounts.inferred} inferred` : ''}
              </span>
            } />
            {coverageWarning && (
              <div role="status" style={{ padding: '18px 16px', fontSize: 11, color: C.amb, fontFamily: FONTS.mono }}>
                {coverageWarning}
                {directoryAssetIdentity(asset) && ' This API does not provide enough evidence to assign findings to canonical directory objects.'}
                <Button variant="ghost" onClick={() => navigate('/client/findings')}>Review all findings</Button>
                {directoryAssetIdentity(asset) && <Button variant="ghost" onClick={() => navigate('/client/ad-security')}>Review AD Security</Button>}
                <Button variant="ghost" onClick={refreshFindings}>Refresh compatibility check</Button>
              </div>
            )}
            {(findingsError || ownershipIncomplete) && (
              <div role="alert" style={{ padding: '18px 16px', fontSize: 11, color: C.amb, fontFamily: FONTS.mono }}>
                Complete finding history is unavailable. Loaded rows are retained; refresh to retry.
                <Button variant="ghost" onClick={refreshFindings}>Refresh findings</Button>
              </div>
            )}
            {findingsLoading && totalLinked === 0 ? (
              <div style={{ padding: 16 }}>
                <Skeleton height={90} />
              </div>
            ) : totalLinked === 0 && findingsComplete && backendFindingTotal === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: C.grn }}><CheckCircle2 size={28} strokeWidth={1.5} /></div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.grn, fontFamily: FONTS.sans }}>No Linked Findings</div>
                <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, marginTop: 4 }}>No asset-specific finding links were returned</div>
              </div>
            ) : (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                    {['Severity', 'Title', 'Status', 'Discovered', 'Source', 'Link'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left', background: C.s2 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleLinkedGroups.map(g => {
                    const f = g.first;
                    const open = expandedGroups.has(g.key);
                    const visibleFindings = g.findings.slice(0, expandedLimits[g.key] ?? MAX_EXPANDED_FINDINGS_RENDERED);
                    return (
                      <Fragment key={g.key}>
                        <TR onClick={() => g.count > 1 ? toggleGroup(g.key) : navigate(`/client/findings/${f.id}`)}>
                          <TD><SeverityBadge value={g.sev} /></TD>
                          <TD style={{ maxWidth: 280 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.t1, fontWeight: 500 }}>
                              {g.count > 1 ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span style={{ width: 12 }} />}
                              <span>{f.title}</span>
                              {g.count > 1 && <span style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, fontWeight: 800 }}>({g.count})</span>}
                            </div>
                            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{targetLabel(f, g.count) || f.module || '\u2014'}</div>
                          </TD>
                          <TD><StatusBadge value={f.status} /></TD>
                          <TD>
                            <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={f.age ? exactTime(f.age) : ''}>
                              {f.age ? relativeTime(f.age) : '\u2014'}
                            </span>
                          </TD>
                          <TD><span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{f.module || '\u2014'}</span></TD>
                          <TD><LinkBadge type={f.linkType} reason={f.linkReason} /></TD>
                        </TR>
                        {open && g.count > 1 && (
                          <tr style={{ background: C.s2 }}>
                            <td colSpan={6} style={{ padding: '10px 16px 14px 34px' }}>
                              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Grouped instances ({g.count})</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {visibleFindings.map(item => (
                                  <div key={item.id} onClick={() => navigate(`/client/findings/${item.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontFamily: FONTS.mono, color: C.t1, background: C.s1, border: `1px solid ${C.b2}` }}>
                                    <span style={{ wordBreak: 'break-all', flex: 1 }}>{locationOf(item) || item.asset || item.module}</span>
                                    <LinkBadge type={item.linkType} reason={item.linkReason} />
                                    <StatusBadge value={item.status} />
                                    <span style={{ fontSize: 10, color: C.t3 }}>{'\u2192'}</span>
                                  </div>
                                ))}
                                {g.findings.length > visibleFindings.length && (
                                  <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, padding: '6px 10px' }}>
                                    Showing {visibleFindings.length} of {g.findings.length} grouped instances.
                                    <Button variant="ghost" onClick={() => setExpandedLimits(previous => ({
                                      ...previous, [g.key]: (previous[g.key] ?? MAX_EXPANDED_FINDINGS_RENDERED) + MAX_EXPANDED_FINDINGS_RENDERED,
                                    }))}>Show more loaded instances</Button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {hasMoreFindings && !findingsError && (
              <div style={{ padding: 12 }}>
                <Button variant="ghost" disabled={findingsLoading} onClick={loadMore}>
                  {findingsLoading ? 'Loading more findings...' : findingsMode === 'legacy' ? 'Check more tenant findings' : 'Load more asset findings'}
                </Button>
              </div>
            )}
            {linkedGroupsTruncated && (
              <div style={{ fontSize: 10, color: C.t3, padding: 12 }}>
                Showing first {visibleGroupLimit} loaded finding groups.
                <Button variant="ghost" onClick={() => setVisibleGroupLimit(limit => limit + MAX_FINDING_GROUPS_RENDERED)}>
                  Show more loaded groups
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Section 4: Risk Summary */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Risk Summary{!findingsComplete || ownershipIncomplete ? ' (loaded findings only)' : ''}</div>
            {totalLinked === 0 ? (
              <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, textAlign: 'center', padding: '12px 0' }}>{findingsComplete && backendFindingTotal === 0 ? 'No findings' : 'Finding history is not fully verified'}</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                  {[
                    { l: 'Critical', n: sevCounts.critical, c: C.red },
                    { l: 'High', n: sevCounts.high, c: C.org },
                    { l: 'Medium', n: sevCounts.medium, c: C.amb },
                    { l: 'Low', n: sevCounts.low, c: C.sky },
                  ].map(s => (
                    <div key={s.l} style={{ padding: '8px 10px', background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}`, textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.n > 0 ? s.c : C.t3, fontFamily: FONTS.mono }}>{s.n}</div>
                      <div style={{ fontSize: 8, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase' }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                {/* Severity breakdown bar */}
                <div style={{ height: 6, borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                  {[
                    { n: sevCounts.critical, c: C.red },
                    { n: sevCounts.high, c: C.org },
                    { n: sevCounts.medium, c: C.amb },
                    { n: sevCounts.low, c: C.sky },
                    { n: sevCounts.info, c: C.gry },
                  ].filter(s => s.n > 0).map((s, i) => (
                    <div key={i} style={{ flex: s.n, height: '100%', background: s.c }} />
                  ))}
                </div>
              </>
            )}
          </Card>

          {/* Asset Details sidebar */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Asset Details</div>
            {[
              { l: 'Type', v: typeLabel },
              { l: 'Lifecycle', v: lifecycle.label },
              { l: 'First Seen', v: asset.firstSeenAt ? exactTime(new Date(asset.firstSeenAt)) : '\u2014' },
              { l: 'Last Seen', v: lastSeenAt ? exactTime(lastSeenAt) : '\u2014' },
              { l: 'Findings', v: `${linkedFindings.length} loaded / ${backendFindingTotal ?? 'unknown'} total` },
              { l: 'Relationships', v: clientGraphLabel(relationshipSummary.total) },
              { l: 'Confirmed Links', v: linkCounts.authoritative },
              { l: 'Inferred Links', v: linkCounts.inferred },
            ].map(r => (
              <div key={r.l} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 2 }}>{r.l}</div>
                <div style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{r.v}</div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Shell>
  );
}

